import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../../utils/ThemeSwitcher';

const MissingDataTimeline = ({ data, height = 300 }) => {

  const containerRef = useRef();
  const ref = useRef();
  const { isDarkMode } = useTheme();
  const [width, setWidth] = useState(600);

  // Effect to handle responsive width with ResizeObserver
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        if (newWidth > 0) {
          setWidth(newWidth);
        }
      }
    };

    // Initial width calculation
    updateWidth();

    // Use ResizeObserver for better performance
    let resizeObserver;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          const newWidth = entries[0].contentRect.width;
          if (newWidth > 0) {
            setWidth(newWidth);
          }
        }
      });
      resizeObserver.observe(containerRef.current);
    }

    // Fallback to window resize event
    const handleResize = () => {
      requestAnimationFrame(updateWidth);
    };
    window.addEventListener('resize', handleResize);

    // Recalculate after a short delay to handle initial render
    const timeout = setTimeout(updateWidth, 100);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (!data || !ref.current) return;

    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    let timelineData = [];
    
    // Check if we have real timeline data from the backend
    if (data.timeline_data && data.timeline_data.length > 0) {
      // Use real timeline data
      timelineData = data.timeline_data.map((item, index) => ({
        day: index + 1,
        missingRate: item.missing_rate,
        date: new Date(item.date),
        observations: item.total_observations,
        missingCount: item.missing_count,
        totalValues: item.total_values,
        label: new Date(item.date).toLocaleDateString()
      }));
      
      console.log('Using real timeline data:', timelineData.length, 'data points');
    } else {
      // Fallback: Calculate average missing rate and show flat line
      const variables = Object.keys(data).filter(key => 
        key !== 'timeline_data' && key !== 'has_timeline' && typeof data[key] === 'object'
      );
      
      let avgMissingRate = 0;
      if (variables.length > 0) {
        const totalMissingRate = variables.reduce((sum, variable) => {
          const varData = data[variable];
          return sum + (varData?.missing_percentage || 0);
        }, 0);
        avgMissingRate = totalMissingRate / variables.length;
      }
      
      // Create flat line with average rate
      timelineData = Array.from({length: 7}, (_, i) => ({
        day: i + 1,
        missingRate: avgMissingRate,
        date: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000),
        label: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000).toLocaleDateString(),
        observations: 0,
        missingCount: 0,
        totalValues: 0
      }));
      
      console.log('Using fallback flat line with average rate:', avgMissingRate);
    }

    if (timelineData.length === 0) return;

    const margin = { top: 30, right: 40, bottom: 70, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const x = d3.scaleLinear()
      .domain([1, timelineData.length])
      .range([0, innerWidth]);

    const maxRate = Math.max(d3.max(timelineData, d => d.missingRate) || 1, 1);
    const y = d3.scaleLinear()
      .domain([0, maxRate * 1.1])
      .range([innerHeight, 0]);

    const line = d3.line()
      .x((d, i) => x(i + 1))
      .y(d => y(d.missingRate))
      .curve(d3.curveMonotoneX);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Add modern grid lines
    const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0)' : 'rgba(0, 0, 0, 0.05)';
    
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(y)
        .tickSize(-innerWidth)
        .tickFormat('')
      )
      .style('stroke', gridColor)
      .style('stroke-width', '0px')   // horizontal lines
      .selectAll('line')
      .style('stroke-dasharray', '0');

    // Create modern tooltip
    const tooltip = d3.select('body').append('div')
      .attr('class', 'missing-timeline-tooltip')
      .style('position', 'absolute')
      .style('visibility', 'hidden')
      .style('background', isDarkMode 
        ? 'linear-gradient(135deg, rgba(23, 23, 27, 0.98) 0%, rgba(13, 14, 14, 0.98) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%)')
      .style('color', isDarkMode ? '#F1F5F9' : '#0F172A')
      .style('padding', '16px 20px')
      .style('border', isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)')
      .style('border-radius', '12px')
      .style('box-shadow', isDarkMode 
        ? '0 12px 40px rgba(0, 0, 0, 0.4)'
        : '0 12px 40px rgba(0, 0, 0, 0.15)')
      .style('font-size', '13px')
      .style('pointer-events', 'none')
      .style('z-index', '1000')
      .style('backdrop-filter', 'blur(20px)');

    // Add gradient for area under curve
    const defs = svg.append('defs');
    const gradientId = `area-gradient-${Math.random().toString(36).substr(2, 9)}`;
    
    const gradient = defs.append('linearGradient')
      .attr('id', gradientId)
      .attr('x1', '0%')
      .attr('x2', '0%')
      .attr('y1', '0%')
      .attr('y2', '100%');

    gradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', isDarkMode ? 'transparent' : '#7ba7eeff')
      .attr('stop-opacity', 0.3);

    gradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#ddddddff')
      .attr('stop-opacity', 0);

    // Add area under curve for better visualization
    if (timelineData.some(d => d.missingRate > 0)) {
      const area = d3.area()
        .x((d, i) => x(i + 1))
        .y0(y(0))
        .y1(d => y(d.missingRate))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(timelineData)
        .attr('fill', `url(#${gradientId})`)
        .attr('d', area)
        .style('opacity', 0)
        .transition()
        .delay(600)
        .duration(500)
        .style('opacity', 1);
    }
    
    // Add line with gradient stroke
    const lineGradientId = `line-gradient-${Math.random().toString(36).substr(2, 9)}`;
    const lineGradient = defs.append('linearGradient')
      .attr('id', lineGradientId)
      .attr('x1', '0%')
      .attr('x2', '100%')
      .attr('y1', '0%')
      .attr('y2', '0%');

    lineGradient.append('stop')
      .attr('offset', '0%')
      .attr('stop-color', '#3B82F6');

    lineGradient.append('stop')
      .attr('offset', '100%')
      .attr('stop-color', '#8B5CF6');

    const linePath = g.append('path')
      .datum(timelineData)
      .attr('fill', 'none')
      .attr('stroke', `url(#${lineGradientId})`)
      .attr('stroke-width', 3)
      .attr('d', line)
      .attr('stroke-linecap', 'round')
      .style('filter', 'drop-shadow(0 2px 8px rgba(59, 130, 246, 0.3))');

    // Line draw animation
    const totalLength = linePath.node().getTotalLength();
    linePath
      .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
      .attr('stroke-dashoffset', totalLength)
      .transition()
      .duration(1000)
      .ease(d3.easeCubicOut)
      .attr('stroke-dashoffset', 0);

    // Add dots with enhanced interactivity — staggered fade-in
    g.selectAll('.dot')
      .data(timelineData)
      .enter().append('circle')
      .attr('class', 'dot')
      .attr('cx', (d, i) => x(i + 1))
      .attr('cy', d => y(d.missingRate))
      .attr('r', 0)
      .attr('fill', 'transparent')
      .attr('stroke', '#3B82F6')
      .attr('stroke-width', 3)
      .style('cursor', 'pointer')
      .style('filter', 'drop-shadow(0 2px 8px rgba(59, 130, 246, 0.4))')
      .transition()
      .delay((d, i) => 400 + i * 80)
      .duration(400)
      .ease(d3.easeBackOut.overshoot(1.5))
      .attr('r', 6);

    // Re-attach mouse events on dots after transition
    g.selectAll('.dot')
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('r', 8)
          .attr('stroke-width', 4)
          .style('filter', 'drop-shadow(0 4px 12px rgba(59, 130, 246, 0.6))');
          
        const tooltipContent = data.timeline_data && data.timeline_data.length > 0 
          ? `<div style="margin-bottom: 8px; font-weight: 700; font-size: 14px;">${d.label}</div>
             <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
               <span style="color: ${isDarkMode ? '#94A3B8' : '#64748B'};">Missing Rate:</span>
               <span style="font-weight: 600; color: #3B82F6;">${d.missingRate.toFixed(1)}%</span>
             </div>
             <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
               <span style="color: ${isDarkMode ? '#94A3B8' : '#64748B'};">Observations:</span>
               <span style="font-weight: 600;">${d.observations}</span>
             </div>
             <div style="display: flex; justify-content: space-between;">
               <span style="color: ${isDarkMode ? '#94A3B8' : '#64748B'};">Missing Values:</span>
               <span style="font-weight: 600;">${d.missingCount}/${d.totalValues}</span>
             </div>`
          : `<div style="margin-bottom: 8px; font-weight: 700; font-size: 14px;">${d.label}</div>
             <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
               <span style="color: ${isDarkMode ? '#94A3B8' : '#64748B'};">Missing Rate:</span>
               <span style="font-weight: 600; color: #3B82F6;">${d.missingRate.toFixed(1)}%</span>
             </div>
             <div style="font-style: italic; color: ${isDarkMode ? '#64748B' : '#94A3B8'}; font-size: 12px; margin-top: 8px;">
               Average across all days
             </div>`;
             
        tooltip.style('visibility', 'visible').html(tooltipContent);
      })
      .on('mousemove', function(event) {
        tooltip
          .style('top', (event.pageY - 10) + 'px')
          .style('left', (event.pageX + 10) + 'px');
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('r', 6)
          .attr('stroke-width', 3)
          .style('filter', 'drop-shadow(0 2px 8px rgba(59, 130, 246, 0.4))');
        tooltip.style('visibility', 'hidden');
      });

    // Add axes with better styling
    const xAxis = g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x)
        .tickFormat(d => {
          const dataPoint = timelineData[d - 1];
          if (dataPoint && dataPoint.date) {
            return dataPoint.date.toLocaleDateString('en-US', { 
              month: 'short', 
              day: 'numeric' 
            });
          }
          return `Day ${d}`;
        })
      );

    xAxis.selectAll('text')
      .style('fill', isDarkMode ? '#94A3B8' : '#64748B')
      .style('font-size', '12px')
      .style('font-weight', '500')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');

    const yAxis = g.append('g')
      .call(d3.axisLeft(y)
        .ticks(6)
        .tickFormat(d => `${d.toFixed(1)}%`))
      .selectAll('text')
      .style('fill', isDarkMode ? '#94A3B8' : '#64748B')
      .style('font-size', '12px')
      .style('font-weight', '500');

    // Style axis lines
    g.selectAll('.domain')
      .style('stroke', isDarkMode ? 'rgba(56, 56, 56, 0)' : 'rgba(0, 0, 0, 0)')   // region outline
      .style('stroke-width', '2px');
    
    g.selectAll('.tick line')
      .style('stroke', isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)');

    // Add modern axis labels with better typography
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -48)
      .attr('text-anchor', 'middle')
      .style('font-size', '13px')
      .style('font-weight', '600')
      .style('fill', isDarkMode ? '#94A3B8' : '#64748B')
      .style('letter-spacing', '0.02em')
      .text('Missing Data Rate (%)');

    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + 58)
      .attr('text-anchor', 'middle')
      .style('font-size', '13px')
      .style('font-weight', '600')
      .style('fill', isDarkMode ? '#94A3B8' : '#64748B')
      .style('letter-spacing', '0.02em')
      .text('Collection Date');

    svg.attr('width', width).attr('height', height);

    // Cleanup
    return () => {
      tooltip.remove();
    };

  }, [data, width, height, isDarkMode]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      <svg 
        ref={ref} 
        style={{ 
          borderRadius: '12px', 
          display: 'block',
          width: '100%',
          maxWidth: '100%',
          height: 'auto'
        }} 
      />
    </div>
  );
};

export default MissingDataTimeline;